import { useState, useCallback } from 'react';
import { uploadToTempStorage, validateImageFile } from '../utils/imageUtils';

export const useImageProcessing = () => {
  const [uploadedImages, setUploadedImages] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);

  const uploadImage = useCallback(async (file) => {
    try {
      setProcessing(true);
      setError(null);
      
      // Validate file
      validateImageFile(file);
      
      // Upload to temporary storage
      const uploadResult = await uploadToTempStorage(file);
      
      const imageData = {
        id: Date.now() + Math.random(),
        file,
        url: uploadResult.url,
        deleteUrl: uploadResult.deleteUrl,
        name: file.name,
        size: file.size,
        type: file.type,
      };
      
      setUploadedImages(prev => [...prev, imageData]);
      setProcessing(false);
      
      return imageData;
    } catch (err) {
      setError(err.message);
      setProcessing(false);
      throw err;
    }
  }, []);

  const uploadMultipleImages = useCallback(async (files) => {
    const results = [];
    for (const file of files) {
      try {
        const result = await uploadImage(file);
        results.push(result);
      } catch (err) {
        console.error(`Failed to upload ${file.name}:`, err);
        results.push({ file, error: err.message });
      }
    }
    return results;
  }, [uploadImage]);

  const removeImage = useCallback((imageId) => {
    setUploadedImages(prev => {
      const imageToRemove = prev.find(img => img.id === imageId);
      
      // Cleanup blob URL if it exists
      if (imageToRemove && imageToRemove.url.startsWith('blob:')) {
        URL.revokeObjectURL(imageToRemove.url);
      }
      
      return prev.filter(img => img.id !== imageId);
    });
  }, []);

  const clearAllImages = useCallback(() => {
    // Cleanup blob URLs
    uploadedImages.forEach(img => {
      if (img.url.startsWith('blob:')) {
        URL.revokeObjectURL(img.url);
      }
    });
    
    setUploadedImages([]);
  }, [uploadedImages]);

  const updateImageData = useCallback((imageId, updates) => {
    setUploadedImages(prev =>
      prev.map(img => 
        img.id === imageId ? { ...img, ...updates } : img
      )
    );
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    uploadedImages,
    processing,
    error,
    uploadImage,
    uploadMultipleImages,
    removeImage,
    clearAllImages,
    updateImageData,
    clearError,
  };
};
